import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";

import Skeleton from "../components/states/Skeleton";
import { getUserMemberships, isStaff, useAuth, type Membership } from "../lib/auth";
import { setActiveWorkspace } from "../lib/tenantContext";

import "./ChooseWorkspace.css";

/**
 * MER-532: the "Choose your workspace" interstitial. RootResolver routes here
 * when a signed-in user has more than one active membership and no active /
 * default workspace, instead of dropping them onto an arbitrary tenant. The
 * user picks one; we record it as the session's active workspace
 * (lib/tenantContext) and route to /<slug>.
 *
 * Standalone route only (no shell, no grid): the in-shell persistent tenant
 * switcher is the deferred follow-up that lands after MER-528. The page is
 * mounted under AuthGate, so an unauthenticated visitor never reaches it.
 *
 * Degenerate cases are handled defensively even though RootResolver only sends
 * multi-membership users here: zero memberships bounce to "/" (RootResolver
 * renders ComingSoon), and a single membership auto-selects so we never make a
 * user "choose" between one option.
 *
 * Staff: RootResolver never bounces a staff user here (staff -> /admin precedes
 * the picker in resolveRoot), but a staff user could MANUALLY hit this route
 * (AuthGate, not StaffGate, gates it). Short-circuit them to /admin so the
 * workspace picker is never their surface, matching the "/" precedence.
 */
export default function ChooseWorkspace() {
  const { session, isLoading } = useAuth();
  const navigate = useNavigate();
  const staff = isStaff(session);
  const userId = session?.user?.id ?? null;
  // undefined = still loading the membership list.
  const [memberships, setMemberships] = useState<Membership[] | undefined>(undefined);

  useEffect(() => {
    // Staff don't get a workspace picker (they're redirected to /admin below),
    // so skip the membership fetch for them, mirroring RootResolver.
    if (isLoading || !userId || staff) return;
    let cancelled = false;
    setMemberships(undefined);
    getUserMemberships()
      .then((m) => {
        if (!cancelled) setMemberships(m);
      })
      .catch(() => {
        if (!cancelled) setMemberships([]);
      });
    return () => {
      cancelled = true;
    };
  }, [isLoading, userId, staff]);

  function choose(slug: string) {
    setActiveWorkspace(userId, slug);
    navigate(`/${slug}`, { replace: true });
  }

  // Staff -> /admin (their home), never the workspace picker. Mirrors the
  // staff branch in resolveRoot so the "/" and manual-/choose-workspace paths
  // agree.
  if (staff) return <Navigate to="/admin" replace />;

  if (isLoading || memberships === undefined) {
    return (
      <main className="choose-workspace">
        <Skeleton label="Loading your workspaces" height={80} />
      </main>
    );
  }

  // Unprovisioned: let RootResolver own the ComingSoon decision at "/".
  if (memberships.length === 0) return <Navigate to="/" replace />;

  // A single workspace is never a "choice": go straight there. No need to set
  // an active workspace; resolveWorkspace lands a lone membership on its slug
  // anyway, and writing during render is a side effect we avoid.
  if (memberships.length === 1) {
    return <Navigate to={`/${memberships[0].slug}`} replace />;
  }

  return (
    <main className="choose-workspace">
      <section className="choose-workspace__card">
        <div className="choose-workspace__logo">
          <span className="choose-workspace__logo-mark">M</span>
          <span className="choose-workspace__logo-text">Meridian Analytics</span>
        </div>
        <h1 className="choose-workspace__title">Choose your workspace</h1>
        <p className="choose-workspace__body">
          You have access to more than one workspace. Pick the one you want to open.
        </p>
        <ul className="choose-workspace__list">
          {memberships.map((m) => (
            <li key={m.tenantId}>
              <button
                type="button"
                className="choose-workspace__option"
                onClick={() => choose(m.slug)}
              >
                <span className="choose-workspace__option-name">{m.name}</span>
                <span className="choose-workspace__option-slug">/{m.slug}</span>
              </button>
            </li>
          ))}
        </ul>
      </section>
    </main>
  );
}
